import { useState, useRef, useEffect } from 'react'
import { useTranslation } from "react-i18next";
import { Image, FloatButton } from 'antd';

import { products, type Product } from '@/data/products.ts';
import { ProductViewsModal } from '@/components/ProductViewsModal';
import { userPhotoRows } from '@/data/userPhotos';

import book1 from '@/assets/book1.jpg';
import book2 from '@/assets/book2.jpg';
import icon1 from '@/assets/icon1.png';
import icon2 from '@/assets/icon2.png';
import icon3 from '@/assets/icon3.png';
import icon4 from '@/assets/icon4.png';
import icon5 from '@/assets/icon5.png';
import bg1 from '@/assets/bg1.png';
import bg2 from '@/assets/bg2.png';
import bg3 from '@/assets/bg3.png';
import bg4 from '@/assets/bg4.png';
import classify1 from '@/assets/classify1.png';
import classify2 from '@/assets/classify2.png';
import classify3 from '@/assets/classify3.png';
import classify4 from '@/assets/classify4.png';
import classify5 from '@/assets/classify5.png';
import classify6 from '@/assets/classify6.png';
import card1Icon from '@/assets/card1-icon.png';
import card2Icon from '@/assets/card2-icon.png';
import card3Icon from '@/assets/card3-icon.png';
import card4Icon from '@/assets/card4-icon.png';
import card5Icon from '@/assets/card5-icon.png';
import card6Icon from '@/assets/card6-icon.png';
import favicon from '@/assets/favicon.png';
import joinIcon from '@/assets/icon-join.png';

import { getItem, removeItem, setItem } from './utils/storage';
import useWindowSizeModel from './models/useWindowSize';
import '../i18n.ts';
import './App.css'

type NavbarData = {
  h5: string,
  android: string,
  ios: string,
  client: string,
  whitePaper: Array<{
    label: string,
    href: string
  }>,
  contactUrl: Array<{
    label: string,
    href: string
  }>
}

const languages = [
  // { code: 'zh', label: '中文' },
  { code: 'en', label: 'English' },
  { code: 'es', label: 'Español' },
  { code: 'de', label: 'Deutsch' },
  { code: 'pt', label: 'Português' },
  { code: 'it', label: 'Italiano' },
  { code: 'tr', label: 'Türkçe' },
  { code: 'ar', label: 'بالعربية' },
  { code: 'id', label: 'Indonesia' },
  { code: 'ms', label: 'Malaysia' },
  { code: 'vi', label: 'Việt Nam' },
  { code: 'fr', label: 'Français' },
]

interface PhotoRowProps {
  photos: string[]
  direction: 'forward' | 'reverse'
  rowIndex: number
  onPhotoClick: (src: string) => void
}

function PhotoRow({ photos, direction, rowIndex, onPhotoClick }: PhotoRowProps) {
  const [hoveredIdx, setHoveredIdx] = useState<number | null>(null)
  const [isPaused, setIsPaused] = useState(false)
  const trackRef = useRef<HTMLDivElement>(null)

  const items = photos.length > 0 ? [...photos, ...photos, ...photos] : []

  const animName = direction === 'forward' ? `scroll-forward-${rowIndex}` : `scroll-reverse-${rowIndex}`

  return (
    <div
      className="photo-row-wrapper"
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => {
        setIsPaused(false)
        setHoveredIdx(null)
      }}
      style={{ overflow: 'hidden', width: '100%', position: 'relative' }}
    >
      <div
        ref={trackRef}
        className={`photo-track ${animName}`}
        style={{
          display: 'flex',
          gap: 'clamp(8px, 2vw, 14px)',
          padding: '8px 0',
          animationPlayState: isPaused ? 'paused' : 'running',
          animationName: direction === 'forward' ? 'scrollLeft' : 'scrollRight',
          animationDuration: '30s',
          animationTimingFunction: 'linear',
          animationIterationCount: 'infinite',
          width: 'max-content',
        }}
      >
        {items.map((src, idx) => (
          <div
            key={`${src}-${idx}`}
            className="photo-avatar"
            role="button"
            tabIndex={0}
            onMouseEnter={() => setHoveredIdx(idx)}
            onMouseLeave={() => setHoveredIdx(null)}
            onClick={() => onPhotoClick(src)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault()
                onPhotoClick(src)
              }
            }}
            style={{
              transition: 'transform 0.3s ease, box-shadow 0.3s ease',
              transform: hoveredIdx === idx ? 'scale(1.1)' : 'scale(1)',
              boxShadow: hoveredIdx === idx
                ? `0 0 0 3px #5174FA, 0 0 16px rgba(81, 116, 250, 0.8)`
                : '0 2px 8px rgba(0,0,0,0.3)',
              border: hoveredIdx === idx ? '2px solid #5174FA' : '2px solid transparent',
            }}
          >
            <img
              src={src}
              alt="user"
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                borderRadius: '50%',
                display: 'block',
              }}
            />
          </div>
        ))}
      </div>
    </div>
  )
}

// White Paper Dropdown (desktop nav)
function WhitePaperDropdown({ navbarData }: { navbarData: NavbarData }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [])

  return (
    <>
      {
        navbarData.whitePaper && navbarData.whitePaper.length !== 0 ? (
          <div ref={ref} className="relative">
            <button
              type="button"
              onMouseEnter={() => setOpen(true)}
              onClick={() => setOpen(v => !v)}
              style={{ fontSize: 'var(--text-base)' }}
              className="text-[#ffffff] transition-colors duration-200 flex items-center gap-1"
            >
              {t('white-paper')}
              <svg className={`w-3 h-3 transition-transform duration-200 ${open ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M19 9l-7 7-7-7" />
              </svg>
            </button>
            {open && (
              <div
                className="absolute top-full left-1/2 -translate-x-1/2 mt-2 bg-white rounded-xl shadow-2xl border border-gray-100 overflow-hidden z-50 min-w-[220px]"
                onMouseLeave={() => setOpen(false)}
              >
                {navbarData.whitePaper.map((item, index) => (
                  <a
                    key={index}
                    href={item.href}
                    target='_blank'
                    className="block px-5 py-3 text-sm text-gray-700 hover:bg-[#5174FA]/10 hover:text-black transition-colors duration-150 font-medium"
                  >
                    {item.label}
                  </a>
                ))}
              </div>
            )}
          </div>
        ) : null
      }
    </>
  )
}

// Contact Dropdown (desktop nav)
function ContactDropdown({ navbarData }: { navbarData: NavbarData }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [])

  if (!navbarData.contactUrl || navbarData.contactUrl.length === 0) return null

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onMouseEnter={() => setOpen(true)}
        onClick={() => setOpen(v => !v)}
        style={{ fontSize: 'var(--text-base)' }}
        className="text-[#ffffff] transition-colors duration-200 flex items-center gap-1"
      >
        {t('contact-us')}
        <svg className={`w-3 h-3 transition-transform duration-200 ${open ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M19 9l-7 7-7-7" />
        </svg>
      </button>
      {open && (
        <div
          className="absolute top-full left-1/2 -translate-x-1/2 mt-2 bg-white rounded-xl shadow-2xl border border-gray-100 overflow-hidden z-50 min-w-[220px]"
          onMouseLeave={() => setOpen(false)}
        >
          {navbarData.contactUrl.map((item, index) => (
            <a
              key={index}
              href={item.href}
              target='_blank'
              className="block px-5 py-3 text-sm text-gray-700 hover:bg-[#5174FA]/10 hover:text-black transition-colors duration-150 font-medium"
            >
              {item.label}
            </a>
          ))}
        </div>
      )}
    </div>
  )
}

export default function App() {
  const { t, i18n } = useTranslation();
  const { isMobile } = useWindowSizeModel();
  const [showLangModal, setShowLangModal] = useState(false)
  const [currentLang, setCurrentLang] = useState('English')
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const [mobileWhitePaperOpen, setMobileWhitePaperOpen] = useState(false)
  const [mobileContactOpen, setMobileContactOpen] = useState(false)
  const [productViewsProduct, setProductViewsProduct] = useState<Pick<Product, 'title' | 'views'> | null>(null)
  const [userPhotoPreview, setUserPhotoPreview] = useState<string | null>(null)
  const [navbarData, setNavbarData] = useState<NavbarData>({
    "h5": "",
    "android": "",
    "ios": "",
    "client": "",
    "whitePaper": [],
    "contactUrl": []
  })

  const handleSelectLang = async (lang: typeof languages[0]) => {
    i18n.changeLanguage(lang.code)
    await setItem('language', lang.code)
    setCurrentLang(lang.label)
    setShowLangModal(false)
  }

  const getNavbarData = async () => {
    try {
      const { protocol, hostname, port } = window.location;
      const url = `${protocol}//${hostname}${port ? ':' + port : ''}`
      const jsonUrl = url + '/download-config.json';
      const response = await fetch(jsonUrl);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const result = await response.json();
      setNavbarData(result)
    } catch (err) {
      console.log(err.message);
    }
  };

  const getLang = async () => {
    try {
      const res = await getItem('language')
      if (res) {
        const findIdx = languages.findIndex((item, index) => {
          return item.code === res
        })
        if (findIdx !== -1 && findIdx !== undefined) {
          handleSelectLang(languages[findIdx])
        }
      }
    } catch { }
  }

  useEffect(() => {
    getNavbarData()
    getLang()
  }, [])

  return (
    <div className="app-shell" style={{ background: '#000', color: '#fff', fontFamily: 'Arial, sans-serif', minHeight: '100vh', overflowX: 'hidden' }}>

      {/* NAVBAR */}
      <nav style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        zIndex: 100,
        background: '#191919',
        backdropFilter: 'blur(10px)',
        borderBottom: '1px solid #222',
        height: 'var(--nav-height)',
        display: 'flex',
        alignItems: 'center',
        padding: `0 var(--page-pad-x)`,
        justifyContent: 'space-between',
      }}>
        {/* Logo */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', minWidth: 0 }}>
          <img src="/figma-assets/logo.png" alt="logo" style={{
            width: 'clamp(120px, 38vw, 152px)',
            height: 'auto',
            maxHeight: 'clamp(26px, 8vw, 33px)',
            objectFit: 'contain',
          }} />
        </div>

        {/* Desktop Nav Links */}
        <div className="nav-links" style={{ display: 'flex', gap: 'var(--nav-link-gap)', alignItems: 'center', flexShrink: 0 }}>
          <a href="#" style={{ color: '#fff', textDecoration: 'none', fontSize: 'var(--text-base)', fontWeight: 400, minWidth: 'min(100px, 22vw)', textAlign: 'center' }}>{t('home')}</a>
          <a href="#about" style={{ color: '#fff', textDecoration: 'none', fontSize: 'var(--text-base)', fontWeight: 400, minWidth: 'min(100px, 22vw)', textAlign: 'center' }}>{t('about-us')}</a>
          {navbarData.whitePaper.length > 0 ? (<WhitePaperDropdown navbarData={navbarData} />) : null}
          {navbarData.contactUrl.length > 0 ? (<ContactDropdown navbarData={navbarData} />) : null}
        </div>

        {/* Language + Mobile menu */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          <button
            onClick={() => setShowLangModal(true)}
            style={{
              background: 'transparent',
              border: 'none',
              color: '#fff',
              fontSize: 'var(--text-base)',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              padding: '4px 8px',
              maxWidth: '42vw',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="10" />
              <line x1="2" y1="12" x2="22" y2="12" />
              <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
            </svg>
            {currentLang}
          </button>
          {/* Mobile hamburger */}
          <button
            type="button"
            className="mobile-menu-btn"
            onClick={() => {
              setMobileMenuOpen(o => !o)
              setMobileWhitePaperOpen(false)
              setMobileContactOpen(false)
            }}
            style={{ background: 'none', border: 'none', color: '#fff', cursor: 'pointer', fontSize: 'clamp(22px, 5vw, 26px)', display: 'none', flexShrink: 0 }}
          >
            ☰
          </button>
        </div>
      </nav>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="mobile-menu-panel">
          <a
            href="#"
            onClick={() => { setMobileMenuOpen(false); setMobileWhitePaperOpen(false) }}
            style={{ display: 'block', color: '#fff', textDecoration: 'none', padding: '12px 0', fontSize: 'var(--text-lead)', borderBottom: '1px solid #222' }}
          >
            {t('home')}
          </a>
          <a
            href="#about"
            onClick={() => { setMobileMenuOpen(false); setMobileWhitePaperOpen(false) }}
            style={{ display: 'block', color: '#fff', textDecoration: 'none', padding: '12px 0', fontSize: 'var(--text-lead)', borderBottom: '1px solid #222' }}
          >
            {t('about-us')}
          </a>

          {
            navbarData.whitePaper && navbarData.whitePaper.length !== 0 ? (
              <div style={{ borderBottom: '1px solid #222' }}>
                <button
                  type="button"
                  onClick={() => setMobileWhitePaperOpen(o => !o)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    width: '100%',
                    background: 'none',
                    border: 'none',
                    color: '#fff',
                    cursor: 'pointer',
                    padding: '12px 0',
                    fontSize: 'var(--text-lead)',
                    textAlign: 'left',
                  }}
                >
                  <span>{t('white-paper')}</span>
                  <svg
                    className={`w-4 h-4 shrink-0 transition-transform duration-200 ${mobileWhitePaperOpen ? 'rotate-180' : ''}`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                {mobileWhitePaperOpen && (
                  <div style={{ paddingLeft: '12px', paddingBottom: '10px', display: 'flex', flexDirection: 'column', gap: '2px' }}>
                    {navbarData.whitePaper.map((paper, index) => (
                      <a
                        key={index}
                        href={paper.href}
                        target='_blank'
                        onClick={() => { setMobileMenuOpen(false); setMobileWhitePaperOpen(false) }}
                        style={{
                          display: 'block',
                          color: '#B9C0D4',
                          textDecoration: 'none',
                          padding: '10px 8px',
                          fontSize: 'var(--text-sm)',
                          borderRadius: '8px',
                        }}
                      >
                        {paper.label}
                      </a>
                    ))}
                  </div>
                )}
              </div>
            ) : null
          }

          {
            navbarData.contactUrl && navbarData.contactUrl.length !== 0 ? (
              <div style={{ borderBottom: 'none' }}>
                <button
                  type="button"
                  onClick={() => setMobileContactOpen(o => !o)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    width: '100%',
                    background: 'none',
                    border: 'none',
                    color: '#fff',
                    cursor: 'pointer',
                    padding: '12px 0',
                    fontSize: 'var(--text-lead)',
                    textAlign: 'left',
                  }}
                >
                  <span>{t('contact-us')}</span>
                  <svg
                    className={`w-4 h-4 shrink-0 transition-transform duration-200 ${mobileContactOpen ? 'rotate-180' : ''}`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                {mobileContactOpen && (
                  <div style={{ paddingLeft: '12px', paddingBottom: '10px', display: 'flex', flexDirection: 'column', gap: '2px' }}>
                    {navbarData.contactUrl.map((contact, index) => (
                      <a
                        key={index}
                        href={contact.href}
                        target='_blank'
                        onClick={() => { setMobileMenuOpen(false); setMobileWhitePaperOpen(false); setMobileContactOpen(false) }}
                        style={{
                          display: 'block',
                          color: '#B9C0D4',
                          textDecoration: 'none',
                          padding: '10px 8px',
                          fontSize: 'var(--text-sm)',
                          borderRadius: '8px',
                        }}
                      >
                        {contact.label}
                      </a>
                    ))}
                  </div>
                )}
              </div>
            ) : null
          }
        </div>
      )}

      {/* LANGUAGE MODAL */}
      {showLangModal && (
        <div
          onClick={() => setShowLangModal(false)}
          style={{
            position: 'fixed', inset: 0, zIndex: 200,
            background: 'rgba(0,0,0,0.7)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{
              background: '#111',
              border: '1px solid #333',
              borderRadius: '16px',
              padding: 'var(--modal-pad)',
              minWidth: 'var(--modal-min-w)',
              width: 'var(--modal-min-w)',
              boxShadow: '0 20px 60px rgba(0,0,0,0.8)',
            }}
          >
            <h3 style={{ color: '#fff', fontSize: 'var(--text-modal-title)', fontWeight: 700, marginBottom: '24px', textAlign: 'center' }}>
              {t('select-language')}
            </h3>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
              {languages.map(lang => (
                <button
                  key={lang.code}
                  onClick={() => handleSelectLang(lang)}
                  style={{
                    background: currentLang === lang.label ? '#5174FA' : '#1a1a1a',
                    border: `1px solid ${currentLang === lang.label ? '#5174FA' : '#333'}`,
                    borderRadius: '10px',
                    color: '#fff',
                    padding: '12px 16px',
                    cursor: 'pointer',
                    fontSize: 'var(--text-sm)',
                    fontWeight: currentLang === lang.label ? 700 : 400,
                    transition: 'all 0.2s',
                    textAlign: 'center',
                  }}
                >
                  {lang.label}
                </button>
              ))}
            </div>
            <button
              onClick={() => setShowLangModal(false)}
              style={{
                marginTop: '20px', width: '100%',
                background: 'transparent', border: '1px solid #555',
                borderRadius: '8px', color: '#aaa',
                padding: '10px', cursor: 'pointer', fontSize: 'var(--text-sm)',
              }}
            >
              {t('cancel')}
            </button>
          </div>
        </div>
      )}

      {/* HERO SECTION */}
      <section style={{
        position: 'relative',
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        paddingTop: 'var(--nav-height)',
        overflow: 'hidden',
        textAlign: 'center',
      }}>
        <div style={{
          position: 'absolute', inset: 0,
          backgroundImage: 'url(/figma-assets/hero-bg.png)',
          backgroundSize: 'cover',
          backgroundPosition: 'center top',
          backgroundRepeat: 'no-repeat',
        }} />
        <div style={{
          position: 'absolute', inset: 0,
          background: 'linear-gradient(to bottom, rgba(0,0,0,0.3) 0%, rgba(0,5,30,0.6) 60%, rgba(0,0,0,0.95) 100%)',
        }} />

        <div style={{ position: 'relative', zIndex: 1, maxWidth: '900px', padding: `0 var(--page-pad-x)`, paddingTop: 'clamp(28px, 8vw, 60px)' }}>
          <h1 style={{
            fontSize: 'var(--text-hero)',
            fontWeight: 700,
            lineHeight: 1.2,
            marginBottom: '20px',
            color: '#fff',
          }}>
            {t('the-worlds-largest-provider-of-distributed-computing-power-sharing-services')}
          </h1>
          <p style={{
            fontSize: 'var(--text-hero-sub)',
            color: '#fff',
            marginBottom: '32px',
            opacity: 0.9,
            lineHeight: 1.5,
          }}>
            {t('offering-you-high-performance-computing-services-and-gpu-cloud-hosting-solutions')}
          </p>

          <button style={{
            background: 'transparent',
            border: '1px solid #5174FA',
            borderRadius: '18px',
            color: '#fff',
            padding: 'clamp(6px, 1.5vw, 8px) clamp(24px, 8vw, 40px)',
            fontSize: 'var(--text-btn-cta)',
            cursor: 'pointer',
            marginBottom: '40px',
          }}>
            {t('compute-without-compromise')}
          </button>

          <div style={{ display: 'flex', gap: '16px', justifyContent: 'center', flexWrap: 'wrap', marginBottom: '40px' }}>
            {
              navbarData.h5 ? (
                <a href={navbarData.h5} style={{
                  background: '#191919',
                  border: '1px solid #373737',
                  borderRadius: '10px',
                  color: '#fff',
                  padding: 'clamp(10px, 2vw, 12px) clamp(16px, 4vw, 24px)',
                  fontSize: 'var(--text-btn)',
                  cursor: 'pointer',
                  display: 'flex', alignItems: 'center', gap: '10px',
                  minWidth: 'min(160px, 46vw)',
                }}>
                  <img src={'/favicon.png'} style={{
                    width: 20, height: 20
                  }} />
                  {t('login-h5')}
                </a>
              ) : null
            }

            {
              navbarData.ios ? (
                <a href={navbarData.ios} style={{
                  background: '#191919',
                  border: '1px solid #373737',
                  borderRadius: '10px',
                  color: '#fff',
                  padding: 'clamp(10px, 2vw, 12px) clamp(16px, 4vw, 24px)',
                  fontSize: 'var(--text-btn)',
                  cursor: 'pointer',
                  display: 'flex', alignItems: 'center', gap: '10px',
                  minWidth: 'min(160px, 46vw)',
                }}>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.8-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z" />
                  </svg>
                  <span style={{ textAlign: 'left', lineHeight: 1.2 }}>
                    <div style={{ fontSize: 'var(--text-caption)', opacity: 0.7 }}>{t('download')}</div>
                    {t('apple-ios')}
                  </span>
                </a>
              ) : null
            }

            {
              navbarData.client ? (
                <a href={navbarData.client} style={{
                  background: '#191919',
                  border: '1px solid #373737',
                  borderRadius: '10px',
                  color: '#fff',
                  padding: 'clamp(10px, 2vw, 12px) clamp(16px, 4vw, 24px)',
                  fontSize: 'var(--text-btn)',
                  cursor: 'pointer',
                  display: 'flex', alignItems: 'center', gap: '10px',
                  minWidth: 'min(160px, 46vw)',
                }}>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M3 3h8v8H3zm10 0h8v8h-8zM3 13h8v8H3zm10 0h8v8h-8z" />
                  </svg>
                  <span style={{ textAlign: 'left', lineHeight: 1.2 }}>
                    <div style={{ fontSize: 'var(--text-caption)', opacity: 0.7 }}>{t('download')}</div>
                    {t('windows')}
                  </span>
                </a>
              ) : null
            }

            {navbarData.android ? (
              <a href={navbarData.android} style={{
                background: '#191919',
                border: '1px solid #373737',
                borderRadius: '10px',
                color: '#fff',
                padding: 'clamp(10px, 2vw, 12px) clamp(16px, 4vw, 24px)',
                fontSize: 'var(--text-btn)',
                cursor: 'pointer',
                display: 'flex', alignItems: 'center', gap: '10px',
                minWidth: 'min(160px, 46vw)',
              }}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M6 18c0 .55.45 1 1 1h1v3.5c0 .83.67 1.5 1.5 1.5s1.5-.67 1.5-1.5V19h2v3.5c0 .83.67 1.5 1.5 1.5s1.5-.67 1.5-1.5V19h1c.55 0 1-.45 1-1V8H6v10zM3.5 8C2.67 8 2 8.67 2 9.5v7c0 .83.67 1.5 1.5 1.5S5 17.33 5 16.5v-7C5 8.67 4.33 8 3.5 8zm17 0c-.83 0-1.5.67-1.5 1.5v7c0 .83.67 1.5 1.5 1.5s1.5-.67 1.5-1.5v-7c0-.83-.67-1.5-1.5-1.5zm-4.97-5.84l1.3-1.3c.2-.2.2-.51 0-.71-.2-.2-.51-.2-.71 0l-1.48 1.48C13.85 1.23 12.95 1 12 1c-.96 0-1.86.23-2.66.63L7.88.15c-.2-.2-.51-.2-.71 0-.2.2-.2.51 0 .71l1.31 1.31C7.01 3.07 6 4.62 6 6.5h12c0-1.88-1.01-3.43-2.47-4.34zM10 5H9V4h1v1zm5 0h-1V4h1v1z" />
                </svg>
                <span style={{ textAlign: 'left', lineHeight: 1.2 }}>
                  <div style={{ fontSize: 'var(--text-caption)', opacity: 0.7 }}>{t('download')}</div>
                  {t('android')}
                </span>
              </a>
            ) : null}
          </div>
        </div>
      </section>

      {/* DEVICE EARNING CARDS */}
      <section style={{
        background: 'linear-gradient(180deg, #000 0%, #050520 50%, #000 100%)',
        padding: `clamp(28px, 8vw, 48px) var(--page-pad-x) clamp(56px, 14vw, 100px)`,
      }}>
        <div style={{ maxWidth: '1100px', margin: '0 auto' }}>
          <div className="cards-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 'clamp(20px, 5vw, 40px)' }}>
            {[
              { titleKey: 'device-laptop', icon: card1Icon, price: '≈300$' },
              { titleKey: 'device-desktop', icon: card2Icon, price: '≈1200$' },
              { titleKey: 'device-server-cluster', icon: card3Icon, price: '≈3000$' },
            ].map((item) => (
              <div key={item.titleKey} style={{
                background: '#191919',
                border: '1px solid #373737',
                borderRadius: '10px',
                padding: 'var(--card-inner-pad)',
                display: 'flex',
                flexDirection: 'column',
                gap: '8px',
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
                  <img src={item.icon} alt="icon" style={{
                    width: 'clamp(40px, 12vw, 50px)', height: 'clamp(40px, 12vw, 50px)'
                  }} />
                  <span style={{ color: '#fff', fontWeight: 700, fontSize: 'var(--text-lead)' }}>{t(item.titleKey)}</span>
                </div>
                <div className='flex flex-row pl-[20px]'>
                  <span className='mr-[20px]' style={{ color: '#45FCFF', fontSize: 'var(--text-stat)', fontWeight: 700 }}>{item.price}</span>
                  <div className='flex ' style={{ flexDirection: 'column', gap: '4px' }}>
                    <span style={{ color: '#fff', fontSize: 'var(--text-sm)' }}>{t('annual')}</span>
                    <span style={{ color: '#fff', fontSize: 'var(--text-sm)' }}>{t('income')}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CONNECT TO EARN SECTION */}
      <section style={{
        backgroundImage: `url(${bg1})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center top',
        backgroundRepeat: 'no-repeat',
        padding: `var(--section-pad-connect-y) var(--page-pad-x)`,
        textAlign: 'center',
        position: 'relative',
        overflow: 'hidden',
      }}>
        <div style={{ position: 'relative', maxWidth: '980px', margin: '0 auto' }}>
          <h2 style={{ fontSize: 'var(--text-section)', fontWeight: 700, marginBottom: 'clamp(20px, 5vw, 36px)', lineHeight: 1.2 }}>
            {t('connect-to-earn-title')}
          </h2>
          <p style={{ color: '#fff', fontSize: 'var(--text-lg)', lineHeight: 1.55, marginBottom: '24px' }}>
            {t('connect-to-earn-desc')}
          </p>
          <div style={{
            border: '1px solid #5174FA',
            borderRadius: '10px',
            width: '100%',
            display: 'inline-block',
            fontSize: 'var(--text-panel)',
            color: '#fff',
            lineHeight: 1.45,
            padding: 'clamp(10px, 3vw, 14px) clamp(14px, 4vw, 22px)'
          }}>
            <p style={{
              marginLeft: 'auto',
              marginRight: 'auto',
            }}>
              {t('connect-to-earn-highlight')}
            </p>
          </div>
        </div>
      </section>

      {/* REVOLUTIONARY AI PLATFORM */}
      <section style={{
        padding: `var(--section-pad-y-md) var(--page-pad-x)`,
        background: '#000',
      }}>
        <div style={{ maxWidth: '1100px', margin: '0 auto' }}>
          <div style={{
            background: 'linear-gradient(135deg, #000A33 0%, #0225AC 100%)',
            border: '1px solid #373737',
            borderRadius: '16px',
            padding: 'clamp(24px, 4vw, 48px)',
          }}>
            <div className="ai-platform-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'clamp(20px, 4vw, 32px)', alignItems: 'start' }}>
              <div>
                <h2 style={{ fontSize: 'var(--text-block-title)', fontWeight: 700, marginBottom: '16px', color: '#fff' }}>
                  {t('revolutionary-ai-platform-title')}
                </h2>
                <p style={{ color: '#fff', opacity: 0.7, fontSize: 'var(--text-lead)', lineHeight: 1.5, marginBottom: 'clamp(28px, 6vw, 44px)' }}>
                  {t('revolutionary-ai-platform-desc')}
                </p>
                <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                  {['tag-high-performance', 'tag-customized-solution', 'tag-perfectly-meeting', 'tag-task-requirements'].map((tagKey, i) => {
                    const colors = ['#ECB1FF', '#73EFFF', '#8096FF', '#A0FFB5']
                    return (
                      <span key={tagKey} style={{
                        color: colors[i],
                        fontSize: 'var(--text-tag)',
                        fontWeight: 400,
                        padding: 'clamp(4px, 1vw, 6px) clamp(14px, 3vw, 22px)',
                        borderRadius: 5,
                        border: '1px solid #4647B5'
                      }}>{t(tagKey)}</span>
                    )
                  })}
                </div>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                {[
                  { textKey: 'ai-platform-item-1', icon: card4Icon },
                  { textKey: 'ai-platform-item-2', icon: card5Icon },
                  { textKey: 'ai-platform-item-3', icon: card6Icon },
                ].map((item, i) => (
                  <div className='test-css' key={i} style={{ display: 'flex', alignItems: 'center', border: '1px solid #4647B5', borderRadius: 10, padding: 'clamp(14px, 3vw, 20px) clamp(18px, 4vw, 30px)' }}>
                    <img src={item.icon} alt="icon" style={{
                      width: 'clamp(40px, 10vw, 48px)', height: 'clamp(40px, 10vw, 48px)', marginRight: 'clamp(16px, 3vw, 24px)', flexShrink: 0
                    }} />
                    <p style={{ color: '#ffffff', fontSize: 'var(--text-lead)', lineHeight: 1.45, margin: 0 }}>{t(item.textKey)}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* PRODUCT CARDS */}
      <section style={{ padding: `var(--section-pad-y-md) var(--page-pad-x)`, background: '#000' }}>
        <div style={{ maxWidth: '1100px', margin: '0 auto' }}>
          <div className="products-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 'clamp(12px, 2.5vw, 20px)' }}>
            {products.map((product) => (
              <div key={product.title} style={{
                background: '#191919',
                border: '1px solid #373737',
                borderRadius: '10px',
                overflow: 'hidden',
              }}>
                <div style={{
                  height: '160px',
                  background: '#111',
                  position: 'relative',
                  overflow: 'hidden',
                  borderRadius: '10px 10px 0 0',
                }}>
                  <img
                    src={product.cover}
                    alt={product.title}
                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    onError={(e) => {
                      (e.target as HTMLImageElement).style.display = 'none'
                    }}
                  />
                  <div style={{
                    position: 'absolute',
                    bottom: 0,
                    left: 0,
                    right: 0,
                    height: '60px',
                    background: 'linear-gradient(transparent, rgba(0,0,0,0.7))',
                  }} />
                </div>
                <div style={{ padding: 'clamp(12px, 3vw, 18px)' }}>
                  <div style={{ color: '#fff', fontWeight: 700, fontSize: 'var(--text-lg)', marginBottom: '8px' }}>{product.title}</div>
                  <div style={{ color: '#9AA1AF', fontSize: 'var(--text-sm)', marginBottom: '16px', lineHeight: 1.4 }}>
                    {product.description}
                  </div>
                  <button
                    type="button"
                    onClick={() => setProductViewsProduct({ title: product.title, views: product.views })}
                    style={{
                      background: '#5174FA',
                      border: 'none',
                      borderRadius: '5px',
                      color: '#fff',
                      padding: 'clamp(8px, 2vw, 10px) clamp(16px, 4vw, 22px)',
                      fontSize: 'var(--text-btn)',
                      cursor: 'pointer',
                      width: '100%',
                    }}
                  >
                    {t('view-more')}
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <ProductViewsModal
        product={productViewsProduct}
        onClose={() => setProductViewsProduct(null)}
      />

      {/* REGARDING StellarNet */}
      <section id="about" style={{
        padding: `var(--section-pad-y-lg) var(--page-pad-x)`, background: '#000',
        backgroundImage: `url(${bg2})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center top',
        backgroundRepeat: 'no-repeat',
      }}>
        <div style={{ maxWidth: '1100px', margin: '0 auto' }}>
          <h2 style={{ fontSize: 'var(--text-section)', fontWeight: 700, textAlign: 'center', marginBottom: 'clamp(32px, 6vw, 48px)' }}>
            {t('regarding-stellarnet')}
          </h2>
          <div className="certs-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px', marginBottom: '40px' }}>
            <div className='flex items-center' style={{
              justifyContent: isMobile ? 'center' : 'flex-end'
            }}>
              <Image width={267} height={346} src={book1} />
            </div>
            <div className='flex items-center' style={{
              justifyContent: isMobile ? 'center' : 'flex-start'
            }}>
              <Image width={267} height={346} src={book2} />
            </div>
          </div>

          {[
            { icon: icon1, textKey: 'about-company-registration-text' },
            { icon: icon2, textKey: 'about-state-registration-text' },
            { icon: icon3, textKey: 'about-msb-license-text' },
            { icon: icon4, textKey: 'about-msb-scope-text' },
            { icon: icon5, textKey: 'about-msb-license-viewing-text' },
          ].map((item, i) => (
            <div key={i} className='test-css' style={{
              background: '#191919',
              border: '1px solid #373737',
              borderRadius: '10px',
              padding: 'clamp(18px, 4vw, 26px)',
              marginBottom: '16px',
              display: 'flex',
              gap: 'clamp(14px, 3vw, 22px)',
              alignItems: 'flex-start',
            }}>
              <img src={item.icon} alt="icon" style={{
                width: 'clamp(48px, 12vw, 60px)', height: 'clamp(48px, 12vw, 60px)', flexShrink: 0
              }} />
              <p style={{ color: '#fff', flex: 1, fontSize: 'var(--text-btn)', lineHeight: 1.45, wordBreak: 'break-word' }}>
                {t(item.textKey)}
              </p>
            </div>
          ))}
        </div>
      </section>

      {/* PHOTO WALL - Over 2 million people */}
      <section style={{
        padding: `var(--section-pad-y-lg) 0`,
        background: '#000',
        textAlign: 'center',
        overflow: 'hidden',
      }}>
        <div style={{ padding: `0 var(--page-pad-x)` }}>
          <h2 style={{ fontSize: 'var(--text-section)', fontWeight: 700, marginBottom: '16px', lineHeight: 1.2 }}>
            {t('photo-wall-title-line1')}<br />{t('photo-wall-title-line2')}
          </h2>
          <p style={{ color: '#fff', opacity: 0.9, fontSize: 'var(--text-hero-sub)', marginBottom: 'clamp(32px, 8vw, 48px)', lineHeight: 1.5 }}>
            {t('photo-wall-desc')}
          </p>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', overflow: 'hidden' }}>
          <PhotoRow photos={userPhotoRows[0]} direction="forward" rowIndex={1} onPhotoClick={setUserPhotoPreview} />
          <PhotoRow photos={userPhotoRows[1]} direction="reverse" rowIndex={2} onPhotoClick={setUserPhotoPreview} />
          <PhotoRow photos={userPhotoRows[2]} direction="forward" rowIndex={3} onPhotoClick={setUserPhotoPreview} />
        </div>
        {userPhotoPreview && (
          <Image
            wrapperStyle={{ display: 'none' }}
            src={userPhotoPreview}
            preview={{
              visible: true,
              onVisibleChange: (visible) => {
                if (!visible) setUserPhotoPreview(null)
              },
            }}
          />
        )}
      </section>

      {/* JOIN StellarNet */}
      <section style={{
        padding: `var(--section-pad-y-lg) var(--page-pad-x)`,
        background: '#000',
        position: 'relative',
        overflow: 'hidden',
      }}>
        <div style={{ maxWidth: '1100px', margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: 'clamp(36px, 8vw, 48px)' }}>
            <h2 style={{ fontSize: 'var(--text-section)', fontWeight: 700, lineHeight: 1.2, marginBottom: '24px' }}>
              {t('join-stellarnet-title-line1')}<br />
              {t('join-stellarnet-title-line2')}
            </h2>
            <p style={{ color: '#fff', fontSize: 'var(--text-lg)', lineHeight: 1.6, opacity: 0.9, maxWidth: '900px', margin: '0 auto' }}>
              {t('join-stellarnet-desc')}
            </p>
          </div>
        </div>
      </section>

      {/* USE CASES */}
      <section style={{
        padding: `0 var(--page-pad-x) var(--section-pad-y-md)`, background: '#000'
      }}>
        <div style={{ maxWidth: '1100px', margin: '0 auto' }}>
          <div className="use-cases-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 'clamp(12px, 2.5vw, 20px)' }}>
            {[
              { titleKey: 'use-case-ai-title', descKey: 'use-case-ai-desc', icon: classify1 },
              { titleKey: 'use-case-biotech-title', descKey: 'use-case-biotech-desc', icon: classify2 },
              { titleKey: 'use-case-physics-title', descKey: 'use-case-physics-desc', icon: classify3 },
              { titleKey: 'use-case-astronomy-title', descKey: 'use-case-astronomy-desc', icon: classify4 },
              { titleKey: 'use-case-environment-title', descKey: 'use-case-environment-desc', icon: classify5 },
              { titleKey: 'use-case-math-title', descKey: 'use-case-math-desc', icon: classify6 },
            ].map((item, i) => (
              <div key={i} className='test-css' style={{
                background: '#191919',
                border: '1px solid #373737',
                borderRadius: '16px',
                padding: 'clamp(18px, 4vw, 26px)',
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
                  <img src={item.icon} style={{
                    width: 'clamp(40px, 10vw, 48px)', height: 'clamp(40px, 10vw, 48px)'
                  }} />
                  <span style={{ color: '#fff', fontWeight: 700, fontSize: 'var(--text-lead)' }}>{t(item.titleKey)}</span>
                </div>
                <p style={{ color: '#B9B9B9', fontSize: 'var(--text-btn)', lineHeight: 1.5, margin: 0 }}>
                  {t(item.descKey)}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* COMMUNITY CTA */}
      <section style={{
        padding: `var(--section-pad-y-md) var(--page-pad-x)`, background: '#000',
        backgroundImage: `url(${bg3})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center top',
        backgroundRepeat: 'no-repeat',
      }}>
        <div style={{ maxWidth: '780px', margin: '0 auto' }}>
          <div style={{
            borderRadius: '10px',
            backgroundImage: `url(${bg4})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center top',
            backgroundRepeat: 'no-repeat',
            padding: `clamp(28px, 8vw, 48px) clamp(20px, 6vw, 36px)`,
            textAlign: 'center',
          }}>
            <img src={joinIcon} alt="" style={{
              width: 'clamp(56px, 18vw, 80px)', height: 'clamp(56px, 18vw, 80px)', marginLeft: 'auto', marginRight: 'auto', marginBottom: 'clamp(24px, 6vw, 40px)', objectFit: 'contain'
            }} />
            <h2 style={{ fontSize: 'var(--text-section)', fontWeight: 700, marginBottom: '20px', lineHeight: 1.2 }}>
              {t('join-community-title')}
            </h2>
            <p style={{ color: '#fff', fontSize: 'var(--text-lg)', lineHeight: 1.5, marginBottom: '32px', opacity: 0.9 }}>
              {t('join-community-desc')}
            </p>
            {navbarData.contactUrl.length > 0 && (
              <div className="community-cta-buttons">
                {navbarData.contactUrl.map((contact, index) => (
                  <a
                    key={index}
                    href={contact.href}
                    target="_blank"
                    className="community-cta-btn"
                  >
                    {contact.label}
                  </a>
                ))}
              </div>
            )}
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <section id="footer-hero" style={{
        padding: `var(--section-pad-y-lg) var(--page-pad-x)`,
        background: '#000',
        textAlign: 'center',
        position: 'relative',
        overflow: 'hidden',
        minHeight: 'min(70vh, 560px)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
      }}>
        <div style={{
          position: 'absolute',
          bottom: 0, left: 0, right: 0,
          height: '60%',
          backgroundImage: `
            linear-gradient(rgba(81,116,250,0.15) 1px, transparent 1px),
            linear-gradient(90deg, rgba(81,116,250,0.15) 1px, transparent 1px)
          `,
          backgroundSize: '40px 40px',
          backgroundPosition: 'center bottom',
          perspective: '400px',
          transform: 'perspective(400px) rotateX(30deg)',
          transformOrigin: 'bottom center',
          maskImage: 'linear-gradient(to bottom, transparent 0%, rgba(0,0,0,0.8) 40%, rgba(0,0,0,1) 100%)',
          WebkitMaskImage: 'linear-gradient(to bottom, transparent 0%, rgba(0,0,0,0.8) 40%, rgba(0,0,0,1) 100%)',
        }} />

        <div style={{ position: 'relative', zIndex: 1 }}>
          <h2 style={{
            fontSize: 'var(--text-section-alt)',
            fontWeight: 700,
            marginBottom: '16px',
            lineHeight: 1.2,
            maxWidth: '800px',
            marginLeft: 'auto',
            marginRight: 'auto',
            textAlign: 'center',
          }}>
            {t('compute-without-compromise')}
          </h2>
          <p style={{
            color: '#fff',
            fontSize: 'var(--text-xl)',
            opacity: 0.7,
            marginBottom: 'clamp(32px, 8vw, 48px)',
            maxWidth: 'min(100%, 520px)',
            marginLeft: 'auto',
            marginRight: 'auto',
            lineHeight: 1.5,
          }}>
            {t('footer-tagline')}
          </p>

          <div className="orbit-container">
            <div className="orbit-ring orbit-ring-outer" />
            <div className="orbit-ring orbit-ring-inner" />
            <div className="orbit-dot orbit-dot-1" />
            <div className="orbit-dot orbit-dot-2" />
            <div className="orbit-dot orbit-dot-3" />
            <div className="orbit-center">
              <img src={favicon} alt="icon" style={{
                width: 'clamp(42px, 11vw, 50px)', height: 'clamp(42px, 11vw, 50px)'
              }} />
            </div>
          </div>
        </div>
      </section>

      {/* Footer bar */}
      <footer style={{
        background: '#191919',
        borderTop: '1px solid #222',
        padding: `clamp(16px, 4vw, 22px) var(--page-pad-x)`,
        textAlign: 'center',
      }}>
        <p style={{ color: '#fff', opacity: 0.99, fontSize: 'var(--text-base)', margin: 0 }}>
          {t('copyright')}
        </p>
      </footer>

      <FloatButton.BackTop />
    </div>
  )
}
